import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { WarningIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { TranslateHtml } from '@change-corgi/core-react/i18n';

import type { SocialPostErrorCode } from '../../hooks/useSocialPostUrlForm';

export type ErrorVariant = 'error' | 'warning';

// The two `warning` codes are pre-submit client blocks, the two `error` codes come back from the server on submit.
export const ERROR_VARIANT: Readonly<Record<SocialPostErrorCode, ErrorVariant>> = {
	unsupported_platform: 'warning',
	invalid_link: 'warning',
	video_unavailable: 'error',
	generic_error: 'error',
};

const VARIANT_CLASS: Readonly<Record<ErrorVariant, { text: string; icon: string; badge: string }>> = {
	error: { text: 'text-typography-error', icon: 'text-icon-error', badge: 'bg-fill-error-strong' },
	warning: { text: 'text-typography-warning', icon: 'text-icon-warning', badge: 'bg-fill-warning-strong' },
};

// TranslateHtml: each message leads with a <b>-wrapped sentence.
const TIKTOK_ERROR_MESSAGES: Readonly<Record<SocialPostErrorCode, React.ReactNode>> = {
	unsupported_platform: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.unsupported_platform" />,
	invalid_link: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.invalid_link" />,
	video_unavailable: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.video_unavailable" />,
	generic_error: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.generic_error" />,
};

/** The same codes, worded for a link from any allowlisted source; the generic error needs no rewording. */
const MULTI_SOURCE_ERROR_MESSAGES: Readonly<Record<SocialPostErrorCode, React.ReactNode>> = {
	...TIKTOK_ERROR_MESSAGES,
	unsupported_platform: (
		<TranslateHtml value="corgi.homepage.hero_section.social_post_errors_multi_source.unsupported_platform" />
	),
	invalid_link: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors_multi_source.invalid_link" />,
	video_unavailable: (
		<TranslateHtml value="corgi.homepage.hero_section.social_post_errors_multi_source.link_unavailable" />
	),
};

/**
 * An error's icon and message, for the callout below the input or inline beside the submit button.
 * Renders only phrasing content, so it can sit inside an `<output>`.
 */
export const ErrorMessage: React.FC<Props> = ({ appearance, error, multiSource = false }) => {
	const classes = VARIANT_CLASS[ERROR_VARIANT[error]];

	return (
		<>
			{appearance === 'callout' ? (
				<span
					data-qa="social-post-error-badge"
					className={cn('m-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full', classes.badge)}
				>
					<Text as="span" variant="body" size="medium" fontWeight="bold" className="text-typography-inverse-strong">
						!
					</Text>
				</span>
			) : (
				<WarningIcon size="small" className={cn('mt-0.5 shrink-0', classes.icon)} />
			)}
			<Text as="span" variant="body" size="small" className={classes.text}>
				{(multiSource ? MULTI_SOURCE_ERROR_MESSAGES : TIKTOK_ERROR_MESSAGES)[error]}
			</Text>
		</>
	);
};

type Props = Readonly<{
	appearance: 'callout' | 'inline';
	error: SocialPostErrorCode;
	/** Word the error for any allowlisted link, not only TikTok. */
	multiSource?: boolean;
}>;
